/**
 * Animated icon for the Improve prompt button: glow, rotating gradient shape, inner highlight and a
 * sparkle. CSS-only; respects `prefers-reduced-motion`.
 */
interface ImprovePromptIconProps {
  /** Busy state while the clarifier questions are loading. */
  isActive?: boolean;
}

export const ImprovePromptIcon = ({ isActive = false }: ImprovePromptIconProps) => (
  <div
    className={`improve-prompt-icon${isActive ? 'improve-prompt-icon--active' : ''} relative flex h-5 w-5 items-center justify-center`}
    aria-hidden="true"
  >
    <style>{ICON_STYLES}</style>
    <div className="improve-prompt-glow absolute inset-[-2px] rounded-full" />
    <div className="improve-prompt-blob absolute inset-0" />
    <div className="improve-prompt-blob-alt absolute inset-[18%]" />
    <svg className="improve-prompt-spark absolute inset-[15%]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" fill="white" opacity="0.95" />
    </svg>
  </div>
);

const ICON_STYLES = `
.improve-prompt-icon { overflow: visible; }

/* Same orange → magenta → purple as the Improve prompt modal. */
.improve-prompt-glow {
  background: radial-gradient(
    circle,
    rgba(253, 97, 0, 0.3) 0%,     /* #FD6100 orange */
    rgba(255, 0, 251, 0.22) 45%,  /* #FF00FB magenta */
    rgba(123, 0, 255, 0.15) 75%,  /* #7B00FF purple */
    transparent 100%
  );
  filter: blur(3px);
  animation: improve-prompt-glow-pulse 3.2s ease-in-out infinite;
}

.improve-prompt-blob {
  background: conic-gradient(
    from 0deg,
    #FD6100 0deg,     /* orange */
    #FF00FB 120deg,   /* magenta */
    #7B00FF 240deg,   /* purple */
    #FD6100 360deg    /* orange (loop back) */
  );
  border-radius: 30% 65% 25% 70% / 55% 25% 65% 30%;
  animation:
    improve-prompt-morph 5s ease-in-out infinite,
    improve-prompt-spin 9s linear infinite;
  filter: blur(0.4px);
}

.improve-prompt-blob-alt {
  background: conic-gradient(
    from 180deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 0, 251, 0.7),   /* magenta hot spot */
    rgba(253, 97, 0, 0.85),   /* orange hot spot */
    rgba(255, 255, 255, 0.95)
  );
  border-radius: 65% 25% 55% 35% / 30% 60% 30% 60%;
  animation:
    improve-prompt-morph-alt 4s ease-in-out infinite,
    improve-prompt-spin 5.5s linear infinite reverse;
  mix-blend-mode: screen;
}

.improve-prompt-spark {
  filter: drop-shadow(0 0 1px rgba(255, 255, 255, 0.9));
  animation: improve-prompt-spark-spin 6s linear infinite, improve-prompt-spark-pulse 2s ease-in-out infinite;
}

@keyframes improve-prompt-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

@keyframes improve-prompt-spark-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(-360deg); }
}

@keyframes improve-prompt-morph {
  0%, 100% { border-radius: 30% 65% 25% 70% / 55% 25% 65% 30%; }
  33%      { border-radius: 65% 30% 70% 25% / 25% 65% 30% 55%; }
  66%      { border-radius: 45% 55% 40% 60% / 60% 40% 55% 45%; }
}

@keyframes improve-prompt-morph-alt {
  0%, 100% { border-radius: 65% 25% 55% 35% / 30% 60% 30% 60%; }
  50%      { border-radius: 25% 65% 35% 55% / 60% 30% 60% 30%; }
}

@keyframes improve-prompt-glow-pulse {
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50%      { opacity: 0.85; transform: scale(1.08); }
}

@keyframes improve-prompt-spark-pulse {
  0%, 100% { opacity: 0.75; transform: scale(0.85); }
  50%      { opacity: 1; transform: scale(1.05); }
}

/* Busy state: every layer speeds up on a different cycle, so it doesn't look like a plain spinner. */
.improve-prompt-icon--active {
  animation: improve-prompt-active-jitter 1.3s ease-in-out infinite;
}
.improve-prompt-icon--active .improve-prompt-blob {
  animation:
    improve-prompt-morph 0.65s ease-in-out infinite,
    improve-prompt-spin 0.95s linear infinite;
  filter: blur(0.3px);
}
.improve-prompt-icon--active .improve-prompt-blob-alt {
  animation:
    improve-prompt-morph-alt 0.5s ease-in-out infinite,
    improve-prompt-spin 0.7s linear infinite reverse;
}
.improve-prompt-icon--active .improve-prompt-spark {
  animation:
    improve-prompt-active-spark-pulse 0.55s ease-in-out infinite,
    improve-prompt-active-spark-wobble 1.9s ease-in-out infinite;
}
.improve-prompt-icon--active .improve-prompt-glow {
  animation: improve-prompt-active-glow 0.8s ease-in-out infinite;
}

@keyframes improve-prompt-active-jitter {
  0%   { transform: translate(0, 0) rotate(0deg); }
  20%  { transform: translate(0.3px, -0.4px) rotate(1.2deg); }
  40%  { transform: translate(-0.4px, 0.2px) rotate(-1.6deg); }
  60%  { transform: translate(0.2px, 0.5px) rotate(0.9deg); }
  80%  { transform: translate(-0.3px, -0.3px) rotate(-0.7deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
}
@keyframes improve-prompt-active-glow {
  0%, 100% { opacity: 0.75; transform: scale(1);    filter: blur(3px); }
  40%      { opacity: 1;    transform: scale(1.18); filter: blur(4px); }
  70%      { opacity: 0.6;  transform: scale(0.94); filter: blur(2.5px); }
}
@keyframes improve-prompt-active-spark-pulse {
  0%, 100% { opacity: 0.65; transform: scale(0.55); }
  50%      { opacity: 1;    transform: scale(0.95); }
}
@keyframes improve-prompt-active-spark-wobble {
  0%, 100% { filter: drop-shadow(0 0 1.5px rgba(255, 255, 255, 0.9)); }
  50%      { filter: drop-shadow(0 0 4px rgba(255, 0, 251, 0.85)); }
}

@media (prefers-reduced-motion: reduce) {
  .improve-prompt-glow,
  .improve-prompt-blob,
  .improve-prompt-blob-alt,
  .improve-prompt-spark {
    animation: none;
  }
}
`;
